import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
// DTC-L: route bay-rules reviewed_at writes through clock.
import { clock } from "@/utils/clock";
import { usePermissions } from "@/hooks/usePermissions";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle, AlertTriangle, Play, Download } from "lucide-react";
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { getTodayInShopTz } from '@/utils/shopTime';

type ReviewStatus = 'pending' | 'approved' | 'modified';

interface StagingJob {
  id: string;
  job_id: string;
  job_name: string;
  job_category: string;
  ai_primary_bay_type: string | null;
  ai_allowed_bay_types: string[] | null;
  ai_required_capabilities: string[] | null;
  ai_excluded_bay_types: string[] | null;
  confidence: number | null;
  needs_review: boolean | null;
  review_status: ReviewStatus | null;
  final_primary_bay_type: string | null;
  final_allowed_bay_types: string[] | null;
  final_required_capabilities: string[] | null;
  final_excluded_bay_types: string[] | null;
}

const BAY_TYPES = ['flat', 'scissor_lift', '2_post', '4_post', 'alignment_rack', 'adas_bay'];
const CAPABILITIES = ['has_lift', 'supports_wheels_off_ground', 'supports_engine_drop', 'is_alignment', 'is_adas'];

export default function BayRulesReview() {
  const { isManager } = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { shopTimezone } = useShopTimezone();
  const [activeTab, setActiveTab] = useState<'needs_review' | 'pending' | 'approved' | 'modified'>('needs_review');
  const [editingJob, setEditingJob] = useState<string | null>(null);
  const [editState, setEditState] = useState<Partial<StagingJob>>({});
  const [isClassifying, setIsClassifying] = useState(false);

  // Redirect non-managers. Done in an effect (not during render) and the render
  // guard lives after all hooks — an early `return null` before the hooks below
  // would call them conditionally (rules-of-hooks).
  useEffect(() => {
    if (!isManager) navigate('/');
  }, [isManager, navigate]);

  // Fetch global staging jobs (no shop filtering - vendor-side only).
  // Disabled for non-managers so they never trigger the fetch before redirect.
  const { data: stagingJobs, isLoading } = useQuery({
    queryKey: ['bay-staging-jobs', activeTab],
    enabled: isManager,
    queryFn: async () => {
      let query = supabase
        .from('jobs_catalog_bay_staging')
        .select('*')
        .order('confidence', { ascending: true });

      if (activeTab === 'needs_review') {
        query = query.eq('needs_review', true).eq('review_status', 'pending');
      } else if (activeTab === 'pending') {
        query = query.eq('needs_review', false).eq('review_status', 'pending');
      } else {
        query = query.eq('review_status', activeTab);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as StagingJob[];
    },
  });

  const approveMutation = useMutation({
    mutationFn: async ({ jobId, staging }: { jobId: string; staging: StagingJob }) => {
      const finalPrimary = staging.final_primary_bay_type || staging.ai_primary_bay_type;
      const finalAllowed = staging.final_allowed_bay_types || staging.ai_allowed_bay_types;
      const finalCapabilities = staging.final_required_capabilities || staging.ai_required_capabilities;
      const finalExcluded = staging.final_excluded_bay_types || staging.ai_excluded_bay_types;

      // Update staging record
      const { error: stagingError } = await supabase
        .from('jobs_catalog_bay_staging')
        .update({
          final_primary_bay_type: finalPrimary,
          final_allowed_bay_types: finalAllowed,
          final_required_capabilities: finalCapabilities,
          final_excluded_bay_types: finalExcluded,
          review_status: staging.final_primary_bay_type ? 'modified' : 'approved',
          reviewed_at: clock.now().toISOString(),
        })
        .eq('id', staging.id);

      if (stagingError) throw stagingError;

      // Update catalog
      const { error: catalogError } = await supabase
        .from('jobs_catalog')
        .update({
          primary_bay_type: finalPrimary,
          allowed_bay_types: finalAllowed,
          required_capabilities: finalCapabilities,
          excluded_bay_types: finalExcluded,
        })
        .eq('id', jobId);

      if (catalogError) throw catalogError;

      // Log pensieve event (bay classification events don't require session/ticket/tech fields)
      const { data: { user } } = await supabase.auth.getUser();
      const { data: membership } = await supabase
        .from('memberships')
        .select('shop_id')
        .eq('user_id', user?.id)
        .single();

      if (membership) {
        // Core-100 R4 wave 3: the breadcrumb log is server-only; the internal-admin RPC records the approval (it used to be
        // a browser insert RLS refused, and the error was ignored — approvals were silently unrecorded).
        const { error: logError } = await supabase.rpc('record_bay_rules_review' as never, {
          p_shop_id: membership.shop_id,
          p_operation: 'classification_approved',
          p_metadata: {
            job_id: jobId,
            job_name: staging.job_name,
            primary_bay_type: finalPrimary,
            confidence: staging.confidence,
            modified: !!staging.final_primary_bay_type,
          },
        } as never);
        if (logError) throw new Error(`Approved, but the approval record was not written: ${logError.message}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bay-staging-jobs'] });
      toast.success('Bay classification approved');
      setEditingJob(null);
      setEditState({});
    },
    onError: (error) => {
      toast.error('Failed to approve classification: ' + error.message);
    },
  });

  const runClassifier = async () => {
    setIsClassifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('classify-catalog-bays', {
        body: { batchSize: 50, dryRun: false },
      });

      if (error) throw error;

      toast.success(`Classification complete: ${data.processed} jobs processed`);
      queryClient.invalidateQueries({ queryKey: ['bay-staging-jobs'] });
    } catch (error: any) {
      toast.error('Classification failed: ' + error.message);
    } finally {
      setIsClassifying(false);
    }
  };

  const startEditing = (job: StagingJob) => {
    setEditingJob(job.id);
    setEditState({
      final_primary_bay_type: job.final_primary_bay_type || job.ai_primary_bay_type,
      final_allowed_bay_types: job.final_allowed_bay_types || job.ai_allowed_bay_types || [],
      final_required_capabilities: job.final_required_capabilities || job.ai_required_capabilities || [],
      final_excluded_bay_types: job.final_excluded_bay_types || job.ai_excluded_bay_types || [],
    });
  };

  const saveAndApprove = (job: StagingJob) => {
    const updatedJob = { ...job, ...editState };
    approveMutation.mutate({ jobId: job.job_id, staging: updatedJob });
  };

  const approveAsIs = (job: StagingJob) => {
    approveMutation.mutate({ jobId: job.job_id, staging: job });
  };

  const approveAllMutation = useMutation({
    mutationFn: async (jobs: StagingJob[]) => {
      const errors: string[] = [];
      
      for (const job of jobs) {
        const finalPrimary = job.final_primary_bay_type || job.ai_primary_bay_type;
        const finalAllowed = job.final_allowed_bay_types || job.ai_allowed_bay_types;
        const finalCapabilities = job.final_required_capabilities || job.ai_required_capabilities;
        const finalExcluded = job.final_excluded_bay_types || job.ai_excluded_bay_types;

        // Update staging record
        const { error: stagingError } = await supabase
          .from('jobs_catalog_bay_staging')
          .update({
            final_primary_bay_type: finalPrimary,
            final_allowed_bay_types: finalAllowed,
            final_required_capabilities: finalCapabilities,
            final_excluded_bay_types: finalExcluded,
            review_status: job.final_primary_bay_type ? 'modified' : 'approved',
            reviewed_at: clock.now().toISOString(),
          })
          .eq('id', job.id);

        if (stagingError) {
          errors.push(`${job.job_name} (staging): ${stagingError.message}`);
          continue;
        }

        // Update catalog
        const { error: catalogError } = await supabase
          .from('jobs_catalog')
          .update({
            primary_bay_type: finalPrimary,
            allowed_bay_types: finalAllowed,
            required_capabilities: finalCapabilities,
            excluded_bay_types: finalExcluded,
          })
          .eq('id', job.job_id);

        if (catalogError) {
          errors.push(`${job.job_name} (catalog): ${catalogError.message}`);
        }
      }

      if (errors.length > 0) {
        throw new Error(`Failed to approve ${errors.length} jobs:\n${errors.join('\n')}`);
      }

      // Log pensieve event for bulk approval
      const { data: { user } } = await supabase.auth.getUser();
      const { data: membership } = await supabase
        .from('memberships')
        .select('shop_id')
        .eq('user_id', user?.id)
        .single();

      if (membership) {
        const { error: logError } = await supabase.rpc('record_bay_rules_review' as never, {
          p_shop_id: membership.shop_id,
          p_operation: 'bulk_classification_approved',
          p_metadata: {
            count: jobs.length,
            job_ids: jobs.map(j => j.job_id),
          },
        } as never);
        if (logError) throw new Error(`Approved, but the approval record was not written: ${logError.message}`);
      }
    },
    onSuccess: (_, jobs) => {
      queryClient.invalidateQueries({ queryKey: ['bay-staging-jobs'] });
      toast.success(`${jobs.length} jobs approved successfully`);
    },
    onError: (error) => {
      toast.error('Failed to approve all: ' + error.message);
    },
  });

  const approveAll = () => {
    if (!stagingJobs || stagingJobs.length === 0) {
      toast.error('No jobs to approve');
      return;
    }
    
    if (confirm(`Approve all ${stagingJobs.length} jobs in this tab?`)) {
      approveAllMutation.mutate(stagingJobs);
    }
  };

  const exportToCSV = () => {
    if (!stagingJobs || stagingJobs.length === 0) {
      toast.error('No jobs to export');
      return;
    }

    const headers = [
      'Job Name',
      'Category',
      'AI Primary Bay Type',
      'AI Allowed Bay Types',
      'AI Required Capabilities',
      'AI Excluded Bay Types',
      'Confidence',
      'Review Status',
      'Needs Review'
    ];

    const rows = stagingJobs.map(job => [
      job.job_name,
      job.job_category,
      job.ai_primary_bay_type || '',
      job.ai_allowed_bay_types?.join('; ') || '',
      job.ai_required_capabilities?.join('; ') || '',
      job.ai_excluded_bay_types?.join('; ') || '',
      job.confidence ? (job.confidence * 100).toFixed(1) + '%' : '',
      job.review_status || 'pending',
      job.needs_review ? 'Yes' : 'No'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // R9-M-2: Use shop TZ for filename date
    a.download = `bay-rules-${activeTab}-${getTodayInShopTz(shopTimezone)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  // Non-managers are redirected by the effect above; render nothing meanwhile.
  if (!isManager) {
    return null;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Bay Rules Review</h1>
          <p className="text-muted-foreground">Review and approve AI-classified bay requirements</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToCSV} disabled={isLoading || !stagingJobs?.length}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <Button onClick={runClassifier} disabled={isClassifying}>
            {isClassifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Classifying...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Run Classifier
              </>
            )}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList>
          <TabsTrigger value="needs_review">Needs Review</TabsTrigger>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="modified">Modified</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : !stagingJobs?.length ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No jobs found in this category
              </CardContent>
            </Card>
          ) : (
            <>
              {/* CORE04-008: "Approve All" only shown on needs_review tab to prevent re-stamping reviewed_at */}
              {activeTab === 'needs_review' && stagingJobs.length > 0 && (
                <div className="flex justify-end">
                  <Button 
                    onClick={approveAll} 
                    disabled={approveAllMutation.isPending}
                    variant="default"
                  >
                    {approveAllMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Approving {stagingJobs.length} jobs...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Approve All ({stagingJobs.length})
                      </>
                    )}
                  </Button>
                </div>
              )}
              {
            stagingJobs.map((job) => (
              <Card key={job.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>{job.job_name}</CardTitle>
                      <CardDescription>{job.job_category}</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {job.confidence && (
                        <Badge variant={job.confidence >= 0.85 ? 'default' : 'secondary'}>
                          {Math.round(job.confidence * 100)}% confidence
                        </Badge>
                      )}
                      {job.needs_review && (
                        <Badge variant="destructive">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Review Required
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {editingJob === job.id ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Primary Bay Type</Label>
                        <Input
                          value={editState.final_primary_bay_type || ''}
                          onChange={(e) => setEditState({ ...editState, final_primary_bay_type: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Allowed Bay Types</Label>
                        <div className="grid grid-cols-3 gap-2 mt-2">
                          {BAY_TYPES.map((type) => (
                            <div key={type} className="flex items-center space-x-2">
                              <Checkbox
                                checked={editState.final_allowed_bay_types?.includes(type)}
                                onCheckedChange={(checked) => {
                                  const current = editState.final_allowed_bay_types || [];
                                  setEditState({
                                    ...editState,
                                    final_allowed_bay_types: checked
                                      ? [...current, type]
                                      : current.filter((t) => t !== type),
                                  });
                                }}
                              />
                              <label className="text-sm">{type}</label>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label>Required Capabilities</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {CAPABILITIES.map((cap) => (
                            <div key={cap} className="flex items-center space-x-2">
                              <Checkbox
                                checked={editState.final_required_capabilities?.includes(cap)}
                                onCheckedChange={(checked) => {
                                  const current = editState.final_required_capabilities || [];
                                  setEditState({
                                    ...editState,
                                    final_required_capabilities: checked
                                      ? [...current, cap]
                                      : current.filter((c) => c !== cap),
                                  });
                                }}
                              />
                              <label className="text-sm">{cap}</label>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => saveAndApprove(job)} disabled={approveMutation.isPending}>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Save & Approve
                        </Button>
                        <Button variant="outline" onClick={() => setEditingJob(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Primary Bay</Label>
                          <p className="font-medium">{job.ai_primary_bay_type || 'None'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Allowed Bays</Label>
                          <p className="font-medium">{job.ai_allowed_bay_types?.join(', ') || 'None'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Required Capabilities</Label>
                          <p className="font-medium">{job.ai_required_capabilities?.join(', ') || 'None'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Excluded Bays</Label>
                          <p className="font-medium">{job.ai_excluded_bay_types?.join(', ') || 'None'}</p>
                        </div>
                      </div>
                      {activeTab === 'needs_review' && (
                        <div className="flex gap-2 pt-4">
                          <Button onClick={() => approveAsIs(job)} disabled={approveMutation.isPending}>
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Approve
                          </Button>
                          <Button variant="outline" onClick={() => startEditing(job)}>
                            Modify & Approve
                          </Button>
                          <Button variant="ghost">
                            <XCircle className="mr-2 h-4 w-4" />
                            Skip
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
